import { createTheme, type MantineTheme, type MantineColorsTuple } from '@mantine/core';

// Define custom colors with proper MantineColorsTuple typing
const colors: Record<string, MantineColorsTuple> = {
  // Primary color palette (lighter slate, for better visibility against dark backgrounds)
  primary: [
    '#FFFFFF', // 0 - lightest
    '#F8FAFC', // 1
    '#F1F5F9', // 2
    '#E2E8F0', // 3
    '#CBD5E1', // 4
    '#94A3B8', // 5 - base
    '#64748B', // 6
    '#475569', // 7
    '#334155', // 8
    '#1E293B', // 9 - darkest
  ] as MantineColorsTuple,

  // Secondary color (subtle accent similar to shadcn/ui)
  secondary: [
    '#F8F9FA', // 0
    '#F1F3F5', // 1
    '#E9ECEF', // 2
    '#DEE2E6', // 3
    '#CED4DA', // 4
    '#ADB5BD', // 5
    '#868E96', // 6
    '#495057', // 7
    '#343A40', // 8
    '#212529', // 9
  ] as MantineColorsTuple,

  // Success color palette (more vibrant green for better visibility in dark mode)
  success: [
    '#F0FDF4', // 0
    '#DCFCE7', // 1
    '#BBF7D0', // 2
    '#86EFAC', // 3
    '#4ADE80', // 4
    '#22C55E', // 5
    '#16A34A', // 6
    '#15803D', // 7
    '#166534', // 8
    '#14532D', // 9
  ] as MantineColorsTuple,

  // Warning color palette (more vibrant amber)
  warning: [
    '#FFFBEB', // 0
    '#FEF3C7', // 1
    '#FDE68A', // 2
    '#FCD34D', // 3
    '#FBBF24', // 4
    '#F59E0B', // 5
    '#D97706', // 6
    '#B45309', // 7
    '#92400E', // 8
    '#78350F', // 9
  ] as MantineColorsTuple,

  // Error color palette (more visible red for dark mode)
  error: [
    '#FEF2F2', // 0
    '#FEE2E2', // 1
    '#FECACA', // 2
    '#FCA5A5', // 3
    '#F87171', // 4
    '#EF4444', // 5
    '#DC2626', // 6
    '#B91C1C', // 7
    '#991B1B', // 8
    '#7F1D1D', // 9
  ] as MantineColorsTuple,

  // Dark mode background colors
  dark: [
    '#C1C2C5', // 0
    '#A6A7AB', // 1
    '#909296', // 2
    '#5C5F66', // 3
    '#373A40', // 4
    '#2C2E33', // 5
    '#25262B', // 6
    '#1A1B1E', // 7
    '#141517', // 8
    '#101113', // 9
  ] as MantineColorsTuple,
};

// CSS variables resolver for custom theme variables using Mantine's recommended approach
const cssVariablesResolver = (theme: MantineTheme) => ({
  variables: {
    // Common variables for both light and dark modes
    '--mantine-radius-md': theme.radius.md,
    '--mantine-primary': theme.colors.primary[6],
    '--mantine-secondary': theme.colors.secondary[6],
    '--mantine-success': theme.colors.success[6],
    '--mantine-warning': theme.colors.warning[6],
    '--mantine-error': theme.colors.error[6],
  },
  light: {
    // Light mode specific variables using Mantine's naming convention
    '--mantine-card-bg': theme.white,
    '--mantine-card-border': theme.colors.primary[1],
    '--mantine-input-bg': theme.white,
    '--mantine-input-border': theme.colors.primary[4],
    '--mantine-hover-bg': theme.colors.primary[0],
  },
  dark: {
    // Dark mode specific variables using Mantine's naming convention
    '--mantine-card-bg': theme.colors.dark[6],
    '--mantine-card-border': theme.colors.dark[4],
    '--mantine-input-bg': theme.colors.dark[6],
    '--mantine-input-border': theme.colors.dark[4],
    '--mantine-hover-bg': theme.colors.dark[5],
  },
});

// Define custom Mantine theme with enhanced support for dark/light modes
const theme = createTheme({
  // Use custom colors
  colors,

  // Set primary color name
  primaryColor: 'primary',

  // Set a default radius for all components
  defaultRadius: 'md',

  // Define specific radius values to use across the app
  radius: {
    xs: '2px',
    sm: '4px',
    md: '8px',
    lg: '16px',
    xl: '24px',
  },

  // White and black optimized for both light/dark modes
  white: '#ffffff',
  black: '#0F172A', // Slate-900 as black

  // Focus ring style - auto shows focus only when using keyboard navigation
  focusRing: 'auto',

  // Component-specific theme overrides
  components: {
    // Button styles more like shadcn/ui
    Button: {
      defaultProps: {
        color: 'primary',
        variant: 'filled',
      },
      styles: (theme: MantineTheme) => ({
        root: {
          fontWeight: 500,
          boxShadow: 'none',
          height: '40px',
          '&:hover': {
            transform: 'translateY(-1px)',
            transition: 'transform 0.2s ease',
          },
        },
        // Filled button styling
        filled: {
          backgroundColor: theme.colors.primary[7],
          '&:hover': {
            backgroundColor: theme.colors.primary[6],
          },
        },
        // Outline button styling
        outline: {
          borderColor: theme.colors.primary[3],
          color: theme.colors.primary[7],
          '&:hover': {
            backgroundColor: theme.colors.primary[0],
            borderColor: theme.colors.primary[5],
          },
        },
        // Subtle button styling
        subtle: {
          color: theme.colors.primary[7],
          '&:hover': {
            backgroundColor: theme.colors.primary[0],
          },
        },
      }),
    },

    // Card styles more like shadcn/ui with better dark mode
    Card: {
      defaultProps: {
        padding: 'lg',
        shadow: 'sm',
      },
      styles: (theme: MantineTheme) => ({
        root: {
          backgroundColor: 'var(--mantine-card-bg)',
          borderRadius: theme.radius.md,
          border: `1px solid var(--mantine-card-border)`,
          overflow: 'hidden',
          transition: 'border-color 200ms ease, transform 200ms ease',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        },
      }),
    },

    // Input styling with better dark mode support
    TextInput: {
      styles: (theme: MantineTheme) => ({
        input: {
          borderColor: 'var(--mantine-input-border)',
          backgroundColor: 'var(--mantine-input-bg)',
          '&:focus': {
            borderColor: theme.colors.primary[6],
            boxShadow: `0 0 0 2px rgba(100, 116, 139, 0.25)`,
          },
        },
      }),
    },

    // Calendar styling for date pickers
    Calendar: {
      styles: (theme: MantineTheme) => ({
        day: {
          borderRadius: theme.radius.sm,
          '&[data-selected]': {
            backgroundColor: theme.colors.primary[7],
          },
          '&[data-in-range]': {
            backgroundColor: theme.colors.primary[1],
            color: theme.colors.primary[7],
          },
          '&:hover': {
            backgroundColor: 'var(--mantine-hover-bg)',
          },
        },
      }),
    },

    // Switch component with better dark mode
    Switch: {
      styles: (theme: MantineTheme) => ({
        track: {
          backgroundColor: 'var(--mantine-input-border)',
          '&[dataChecked]': {
            backgroundColor: theme.colors.primary[7],
          },
        },
        thumb: {
          borderWidth: 1,
          borderColor: 'var(--mantine-input-border)',
        },
      }),
    },

    // Badge component for status indicators
    Badge: {
      styles: () => ({
        root: {
          textTransform: 'capitalize',
          fontWeight: 600,
          letterSpacing: '0.01em',
        },
      }),
    },

    // Paper component (for backgrounds)
    Paper: {
      defaultProps: {
        shadow: 'sm',
      },
      styles: () => ({
        root: {
          backgroundColor: 'var(--mantine-card-bg)',
          transition: 'background-color 200ms ease',
        },
      }),
    },

    // Improve pagination styling for dark mode
    Pagination: {
      styles: (theme: MantineTheme) => ({
        item: {
          borderColor: 'var(--mantine-card-border)',
          '&[data-active]': {
            backgroundColor: theme.colors.primary[7],
            borderColor: 'transparent',
            color: theme.white,
          },
          '&:hover:not([data-active])': {
            backgroundColor: 'var(--mantine-hover-bg)',
          },
        },
      }),
    },

    // Progress component
    Progress: {
      defaultProps: {
        radius: 'xl', // Set xl radius as default for Progress components
      },
    },

    // ActionIcon component
    ActionIcon: {
      defaultProps: {
        radius: 'xl', // Set xl radius as default for ActionIcon components
      },
    },

    // Menu component
    Menu: {
      defaultProps: {
        shadow: 'sm',
      },
    },

    // Modal component
    Modal: {
      defaultProps: {
        shadow: 'sm',
      },
    },

    // Popover component
    Popover: {
      defaultProps: {
        shadow: 'sm',
      },
    },

    // Drawer component
    Drawer: {
      defaultProps: {
        shadow: 'sm',
      },
    },

    // Tooltip component
    Tooltip: {
      defaultProps: {
        shadow: 'sm',
      },
    },

    // Dropdown component
    Dropdown: {
      defaultProps: {
        shadow: 'sm',
      },
    },
  },

  // Typography settings
  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
  fontFamilyMonospace: 'JetBrains Mono, Monaco, Courier, monospace',

  // Heading styles with Grotesk font
  headings: {
    fontFamily: 'Space Grotesk, Inter, system-ui, -apple-system, sans-serif',
    fontWeight: '600', // Semi-bold headings
    sizes: {
      h1: { fontSize: '2.25rem', lineHeight: '1.2' },
      h2: { fontSize: '1.75rem', lineHeight: '1.3' },
      h3: { fontSize: '1.5rem', lineHeight: '1.4' },
      h4: { fontSize: '1.25rem', lineHeight: '1.5' },
      h5: { fontSize: '1.125rem', lineHeight: '1.5' },
      h6: { fontSize: '1rem', lineHeight: '1.5' },
    },
  },

  // Spacing and sizing
  spacing: { xs: '0.5rem', sm: '0.75rem', md: '1rem', lg: '1.5rem', xl: '2rem' },

  // Consistent shadow styles - enhanced for dark mode
  shadows: {
    xs: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    sm: '0 1px 3px 0 rgba(0,0,0,.25),0 1px 2px -1px rgba(0,0,0,.2)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  },
});

// Export the cssVariablesResolver for use in the Provider
export const mantineCssVariablesResolver = cssVariablesResolver;

// Export the color tokens for use in non-Mantine contexts if needed
export const themeColors = colors;

export default theme;
